import i18n from '../i18n.js';
import { ApiError } from './api.js';

/**
 * Exact API message → i18n key mapping.
 * Only user-facing messages that appear in the frontend are mapped here.
 * Internal/admin-only errors fall through to status-code fallbacks.
 */
const MESSAGE_MAP: Record<string, string> = {
  // ── Auth ────────────────────────────────────────────────────────
  'Invalid credentials': 'errors.auth.invalidCredentials',
  'No active organization memberships': 'errors.auth.noOrganizations',
  'Token expired': 'errors.auth.tokenExpired',
  'Invalid token': 'errors.auth.invalidToken',
  'Not a member of this organization': 'errors.auth.notMember',
  'Invalid or expired pre-auth token': 'errors.auth.expiredPreAuth',
  'Invalid refresh token': 'errors.auth.invalidRefreshToken',
  'User not found': 'errors.auth.userNotFound',
  'Login temporarily unavailable': 'errors.auth.loginUnavailable',
  'Too many failed attempts. Try again later.': 'errors.auth.tooManyAttempts',
  'Missing token': 'errors.auth.missingToken',
  'Authentication temporarily unavailable': 'errors.auth.loginUnavailable',
  'Unauthorized': 'errors.auth.tokenExpired',
  'User ID not found in token': 'errors.auth.invalidToken',

  // ── Patients ───────────────────────────────────────────────────
  'Patient not found': 'errors.patient.notFound',
  'Patient is already blocked': 'errors.patient.alreadyBlocked',
  'Patient is not blocked': 'errors.patient.notBlocked',
  'id (prontuario) and firstName are required': 'errors.patient.idNameRequired',
  'No pending conflicts for this patient': 'errors.patient.noConflicts',
  'No fields to update': 'errors.patient.noFieldsToUpdate',
  'Maximum 100 patient IDs per request': 'errors.patient.tooManyIds',
  'Patient data is blocked per LGPD Art. 18 V': 'errors.patient.lgpdBlocked',
  'Patient is blocked (LGPD Art. 18 V)': 'errors.patient.lgpdBlocked',
  'Paciente vinculado a listas cirurgicas ativas': 'errors.patient.linkedToActiveLists',

  // ── Uploads ────────────────────────────────────────────────────
  'No files uploaded': 'errors.upload.noFiles',
  'Empty upload file': 'errors.upload.emptyFile',
  'Invalid patientId': 'errors.upload.invalidPatientId',
  'Invalid file name': 'errors.upload.invalidFileName',
  'Path traversal detected': 'errors.upload.pathTraversal',

  // ── Surgical Lists ─────────────────────────────────────────────
  'Surgical list not found': 'errors.notFound.surgicalList',
  'Surgical list item not found': 'errors.notFound.listItem',
  'At least one item is required': 'errors.surgicalList.itemRequired',
  'Maximum 50 items per list': 'errors.surgicalList.tooManyItems',
  'Maximum 50 items per batch': 'errors.surgicalList.tooManyItemsBatch',
  'Cannot modify items of a finalized list': 'errors.surgicalList.finalizedItems',
  'Cannot modify metadata of a finalized list': 'errors.surgicalList.finalizedMetadata',
  'Only DRAFT lists can be deleted': 'errors.surgicalList.onlyDraftDelete',
  'Cannot finalize: list is already FINAL': 'errors.surgicalList.alreadyFinal',
  'Schedule returns is only available for CIRURGIA lists': 'errors.surgicalList.returnsOnlyCirurgia',
  'Source list must be finalized before scheduling returns': 'errors.surgicalList.sourceNotFinalized',
  'At least one return is required': 'errors.surgicalList.returnRequired',
  'Preceptor obrigatório para este tipo de atividade quando o médico principal requer supervisão':
    'errors.surgicalList.preceptorRequired',
  'Cannot finalize: validation has blocking issues': 'errors.surgicalList.validationBlocking',

  // ── Users ──────────────────────────────────────────────────────
  'Email already registered': 'errors.user.emailExists',
  'Password must be at least 8 characters': 'errors.user.passwordTooShort',
  'Password must contain uppercase, lowercase, and a digit': 'errors.user.passwordWeak',
  'Member already linked to another user': 'errors.user.memberLinked',
  'Membro já vinculado a outro usuário': 'errors.user.memberLinked',
  'User already has a member in this organization': 'errors.user.alreadyHasMember',
  'Usuário já possui um membro nesta organização': 'errors.user.alreadyHasMember',
  'Registration already registered in this organization': 'errors.user.registrationExists',
  'Duplicate record conflict': 'errors.user.duplicateRecord',
  'User not found in organization': 'errors.user.notFoundInOrg',
  'Cannot modify your own role or permissions': 'errors.user.cannotModifySelf',
  'User is already linked to this organization': 'errors.user.alreadyLinked',

  // ── Exams ──────────────────────────────────────────────────────
  'Template no longer available': 'errors.exam.templateUnavailable',
  'Only draft sessions can be updated': 'errors.exam.onlyDraftUpdate',
  'Only draft sessions can be deleted': 'errors.exam.onlyDraftDelete',
  'Exam session not found': 'errors.exam.sessionNotFound',
  'Exam template not found or inactive': 'errors.exam.templateNotFound',
  'Content is required for TEXT evolutions': 'errors.exam.contentRequired',

  // ── Members ────────────────────────────────────────────────────
  'Member not found': 'errors.notFound.member',
  'Membro não encontrado': 'errors.notFound.member',
  'Nível não encontrado': 'errors.notFound.level',
  'Nível em uso — remova os membros associados primeiro': 'errors.member.levelInUse',
  'Um ou mais IDs de nível são inválidos': 'errors.member.invalidLevelIds',
  'Usuário não encontrado nesta organização': 'errors.user.notFoundInOrg',
  'Template de exame não encontrado': 'errors.notFound.examTemplate',
  'Grupo de membros não encontrado': 'errors.notFound.memberGroup',
  'Protocolo de acompanhamento não encontrado': 'errors.notFound.protocol',
  'Subtipo de consulta não encontrado': 'errors.notFound.appointmentSubtype',
  'Tipo de atividade não encontrado': 'errors.notFound.activityType',
  'Tipo de etapa não encontrado': 'errors.notFound.stageType',
  'Protocolo não encontrado': 'errors.notFound.protocol',
  'Instância de protocolo não encontrada': 'errors.notFound.protocolInstance',

  // ── Document Types ─────────────────────────────────────────────
  'Image file not found': 'errors.notFound.imageFile',
  'File not found': 'errors.notFound.file',

  // ── Protocols ──────────────────────────────────────────────────
  'Cannot restructure protocol steps while active patient journeys exist. Cancel all active journeys first.':
    'errors.protocol.activeJourneys',
  'Cannot delete protocol while active patient journeys exist. Cancel all active journeys first.':
    'errors.protocol.cannotDeleteActive',

  // ── Evolution Notes ────────────────────────────────────────────
  'Evolution note not found': 'errors.notFound.evolutionNote',
  'File is required': 'errors.upload.fileRequired',
  'Type must be PDF or IMAGE': 'errors.upload.invalidType',

  // ── Permissions ────────────────────────────────────────────────
  'Superadmin access required': 'errors.permission.superadminRequired',
};

/**
 * Regex patterns for dynamic API messages.
 * Order matters — first match wins. Put specific patterns before generic ones.
 */
const PATTERN_MAP: ReadonlyArray<[RegExp, string]> = [
  // Not found patterns (most common dynamic errors)
  [/^Patient with id \d+ not found$/, 'errors.notFound.patient'],
  [/^Image \d+ not found/, 'errors.notFound.image'],
  [/^Surgical list \d+ not found$/, 'errors.notFound.surgicalList'],
  [/^Document type \d+ not found$/, 'errors.notFound.documentType'],
  [/^Job (with id )?\d+ not found/, 'errors.notFound.job'],
  [/^Organization \d+ not found/, 'errors.notFound.organization'],
  [/^Override \d+ not found$/, 'errors.notFound.override'],
  [/^Item \d+ not found in list \d+$/, 'errors.notFound.listItem'],
  [/^Patient \d+ not found$/, 'errors.notFound.patient'],
  [/^Membro \d+ não encontrado ou sem nível válido$/, 'errors.member.invalidLevelIds'],
  [/^Membro \d+ não encontrado/, 'errors.notFound.member'],

  // Upload validation
  [/^File too small:/, 'errors.upload.fileTooSmall'],
  [/^File is not a valid PDF:/, 'errors.upload.invalidPdf'],
  [/^File is not a valid image/, 'errors.upload.invalidImage'],
  [/^Unsupported file type:/, 'errors.upload.unsupportedType'],

  // Validation patterns
  [/^Invalid date format:/, 'errors.validation.invalidDateFormat'],
  [/^Invalid date/, 'errors.validation.invalidDate'],
  [/^Missing permission:/, 'errors.permission.missing'],
  [/^Cannot transition from .+ to/, 'errors.surgicalList.invalidTransition'],
  [/^Cannot complete session with status/, 'errors.exam.invalidComplete'],
  [/^Invalid attendance status:/, 'errors.surgicalList.invalidAttendance'],
  [/^Slug ".+" already exists/, 'errors.validation.slugExists'],
  [/^Slug ".+" já existe/, 'errors.validation.slugExists'],
  [/^Required fields missing:/, 'errors.validation.requiredFieldsMissing'],
  [/^Invalid id:/, 'errors.validation.invalidId'],
  [/^Duplicate patient\+eye found/, 'errors.surgicalList.duplicatePatientEye'],
  [/^Invalid patientId:/, 'errors.upload.invalidPatientId'],
  // Catch-all "not found" — exclude auth-like messages (e.g. "User ID not found in token")
  [/(?:^|\s)not found(?:\s|$|\.)/i, 'errors.notFound.generic'],
  [/não encontrad[oa]/, 'errors.notFound.generic'],
];

/** Fallback messages by HTTP status code. */
const STATUS_MAP: Record<number, string> = {
  400: 'errors.status.badRequest',
  401: 'errors.status.unauthorized',
  403: 'errors.status.forbidden',
  404: 'errors.status.notFound',
  409: 'errors.status.conflict',
  422: 'errors.status.badRequest',
  429: 'errors.status.tooManyRequests',
  500: 'errors.status.serverError',
  502: 'errors.status.serverError',
  503: 'errors.status.serverError',
};

/**
 * Translates an API error into a user-friendly i18n message.
 *
 * Resolution order:
 * 1. Exact message match → translated i18n string
 * 2. Regex pattern match → translated i18n string
 * 3. HTTP status code fallback → generic translated string
 * 4. Caller-provided fallback string
 * 5. common.error ("Algo deu errado" / "Something went wrong")
 */
export function translateApiError(error: unknown, fallback?: string): string {
  if (error instanceof ApiError) {
    const msg = error.message;

    // 1. Exact match
    const exactKey = MESSAGE_MAP[msg];
    if (exactKey) return i18n.t(exactKey);

    // 2. Pattern match
    for (const [pattern, key] of PATTERN_MAP) {
      if (pattern.test(msg)) return i18n.t(key);
    }

    // 3. Status code fallback
    const statusKey = STATUS_MAP[error.status];
    if (statusKey) return i18n.t(statusKey);
  }

  // Non-ApiError: plain Error or string
  if (error instanceof Error) {
    // Check if the message itself matches a known API message
    const exactKey = MESSAGE_MAP[error.message];
    if (exactKey) return i18n.t(exactKey);
  }

  if (typeof error === 'string') {
    const exactKey = MESSAGE_MAP[error];
    if (exactKey) return i18n.t(exactKey);
  }

  // 4/5. Caller fallback or generic
  return fallback ?? i18n.t('common.error');
}
